import { useState, useEffect, useRef, useCallback } from "react"
import { BackHandler, Platform } from "react-native"
import {
  PartialState,
  NavigationState,
  NavigationAction,
  createNavigationContainerRef,
  StackActions,
} from "@react-navigation/native"
import * as storage from "../utils/storage"
import { OneSignal, Toast } from "../components"
import {
  AppNavigatorParamList,
  GraphNavigatorParamList,
  HelpNavigatorParamList,
  HomeNavigatorParamList,
  NavigatorParamList,
  ProfileNavigatorParamList,
  QuickInputNavigatorParamList,
} from "./app-navigator"
import { LoginNavigatorParamList } from "./login/login-navigator"
import { HomeCycleNavigatorParamList } from "./home/cycle/cycle-navigator"
import { HomeOverviewNavigatorParamList } from "./home/overview/overview-navigator"
import { HomeSimulationNavigatorParamList } from "./home/simulation/simulation-navigator"
import { HomeAutoFeederPondNavigatorParamList } from "./home/auto-feeder-pond/auto-feeder-pond-navigator"
import JalaAnalytic from "../utils/jala-analytic"
import { MeasurementPredictionNavigatorParamList } from "./prediction/measurement-prediction-navigator"

const variant = require('../config/env.variant').default;

/* eslint-disable */
export const RootNavigation = {
  navigate(_id: string, _name: string, _params?: any) {},
  goBack(_id: string) {},
  resetRoot(_state?: PartialState<NavigationState> | NavigationState, _stack?: object) {},
  getRootState(): NavigationState {
    return {} as any
  },
  dispatch(_action: NavigationAction) {},
}
/* eslint-enable */

export const navigationRef = createNavigationContainerRef()

export const NAVIGATION_PERSISTENCE_KEY = "NAVIGATION_STATE"

/**
 * Gets the current screen from any navigation state.
 */
export function getActiveRouteName(state: NavigationState | PartialState<NavigationState>) {
  const route = state.routes[state.index]

  // Found the active route -- return the name
  if (!route.state) return route.name

  // Recursive call to deal with nested routers
  return getActiveRouteName(route.state)
}

/**
 * Hook that handles Android back button presses and forwards those on to
 * the navigation or allows exiting the app.
 */
export function useBackButtonHandler(canExit: (routeName: string) => boolean) {
  const canExitRef = useRef(canExit)

  useEffect(() => {
    canExitRef.current = canExit
  }, [canExit])

  useEffect(() => {
    // We'll fire this when the back button is pressed on Android.
    const onBackPress = () => {
      if (!navigationRef.isReady()) {
        return false
      }

      // grab the current route
      const routeName = getActiveRouteName(navigationRef.getRootState())

      // are we allowed to exit?
      if (canExitRef.current(routeName)) {
        // exit and let the system know we've handled the event
        if (Platform.OS === 'web') window.close()
        else BackHandler.exitApp()
        return true
      }

      // we can't exit, so let's turn this into a back action
      if (navigationRef.canGoBack()) {
        if (Platform.OS === 'web') window.history.back()
        else navigationRef.goBack()
        return true
      }

      return false
    }

    // Subscribe when we come to life
    if (Platform.OS === 'web')
      window.addEventListener('beforeunload', onBackPress)
    else BackHandler.addEventListener("hardwareBackPress", onBackPress)

    // Unsubscribe when we're done
    return () => {
      if (Platform.OS === 'web')
        window.removeEventListener('beforeunload', onBackPress)
      else BackHandler.removeEventListener("hardwareBackPress", onBackPress)
    }
  }, [])
}

/**
 * Custom hook for persisting navigation state.
 */
export function useNavigationPersistence(storage: any, persistenceKey: string) {
  const [initialNavigationState, setInitialNavigationState] = useState()

  // This feature is particularly useful in development mode.
  // It is selectively enabled in development mode with
  // the following approach. If you'd like to use navigation persistence
  // in production, remove the __DEV__ and set the state to true
  const [isRestored, setIsRestored] = useState(!__DEV__ || variant === 'testing')

  const previousRouteNameRef = useRef<string | undefined>()
  const routeNameRef = useRef<string | undefined>()

  const onNavigationStateChange = (state) => {
    const previousRouteName = routeNameRef.current
    const currentRouteName = getActiveRouteName(state)

    if (previousRouteName !== currentRouteName) {
      // track screens.
      // __DEV__ && console.tron.log(currentRouteName)
      if (__DEV__ || variant === 'testing') {
        Toast.show({ title: currentRouteName, duration: 1000 })
      }
      // Only Mixpanel and AppEvent
      JalaAnalytic.screen(currentRouteName);
      OneSignal.addTrigger("screen", currentRouteName)
    }

    // Save the current route name for later comparision
    previousRouteNameRef.current = previousRouteName
    routeNameRef.current = currentRouteName

    // Persist state to storage
    storage.save(persistenceKey, state)
  }

  const restoreState = useCallback(async () => {
    try {
      const state = await storage.load(persistenceKey)
      if (state) setInitialNavigationState(state)
    } finally {
      setIsRestored(true)
    }
  }, [persistenceKey, storage])

  useEffect(() => {
    if (!isRestored) restoreState().catch()
  }, [isRestored, restoreState])

  return {
    onNavigationStateChange, restoreState, isRestored, initialNavigationState,
    previousRouteNameRef, routeNameRef,
    previousRouteName: previousRouteNameRef.current,
    routeName: routeNameRef.current
  }
}

/**
 * use this to navigate without the navigation
 * prop. If you have access to the navigation prop, do not use this.
 * More info: https://reactnavigation.org/docs/navigating-without-navigation-prop/
 */

type Screen = keyof NavigatorParamList
  | keyof AppNavigatorParamList
  | keyof LoginNavigatorParamList
  | keyof HomeNavigatorParamList
  | keyof GraphNavigatorParamList
  | keyof QuickInputNavigatorParamList
  | keyof HelpNavigatorParamList
  | keyof ProfileNavigatorParamList
  | keyof HomeCycleNavigatorParamList
  | keyof HomeOverviewNavigatorParamList
  | keyof HomeSimulationNavigatorParamList
  | keyof HomeAutoFeederPondNavigatorParamList
  | keyof MeasurementPredictionNavigatorParamList

export function navigate(
  id: string,
  name: Screen,
  params?: {
    screen?: Screen,
    params?: any,
    [key: string]: any
  }
) {
  if (navigationRef.isReady()) {
    navigationRef.navigate(name as never, params as never)
  }
}

export function push(
  name: Screen,
  params?: {
    screen?: Screen,
    params?: any,
    [key: string]: any
  }
) {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(StackActions.push(name, params));
  }
}

export function goBack(id: string) {
  console.log(navigationRef.canGoBack())
  if (navigationRef.isReady() && navigationRef.canGoBack()) {
    navigationRef.goBack()
  } else if (Platform.OS === 'web')
    window.history.back()
}

export function resetRoot(rootStack: any = { index: 0, routes: [] }) {
  if (navigationRef.isReady()) {
    navigationRef.resetRoot({ ...rootStack })
  }
}

export function setParams(id: string, params?: any) {
  if (navigationRef.isReady()) {
    navigationRef.setParams(params as never)
  }
}

export function didFocus(id: string, callback: () => void, navigation) {
  if (navigationRef.isReady()) {
    return navigation.addListener('focus', callback);
  }
  return () => ({});
}

export function resetPersistence() {
  storage.save(NAVIGATION_PERSISTENCE_KEY, null).catch();
}

const bottomTabPressListeners = []

export const bottomTabPressListener = (index) => {
  bottomTabPressListeners.forEach((listener) => {
    listener(index)
  })
}

export function onBottomTabsPressed(callback) {
  bottomTabPressListeners.push(callback)
  return () => {
    const index = bottomTabPressListeners.findIndex(callback)
    bottomTabPressListeners.splice(index, 1)
  }
}

export function hideBottomBar(id: string) {
  //
}

export function showBottomBar(id: string) {
  //
}
